<template>
  <v-form ref="form" @submit.prevent="submit">
    <label>Deal Type</label>
    <v-select
      v-model="formData.dealType"
      solo chips deletable-chips dense
      multiple
      :items="dealTypeOptions"
      item-value="name"
      item-text="title"
      :rules="[
        (v) => !!(v && v.length) || 'At least one is required'
      ]"
      validate-on-blur
    />

    <label>Practice Type</label>
    <v-select
      v-model="formData.practiceType"
      solo chips deletable-chips dense
      :items="questions.practiceTypes"
      item-value="name"
      item-text="title"
      :rules="required"
      validate-on-blur
    />

    <label>Deal Size</label>
    <v-text-field
      v-model="formData.dealSize"
      solo dense
      prefix="$"
      v-mask="currencyMask"
      :rules="[
        v => !!v || 'This field is required.',
        v => parseInt(v.replace(/,/g, '')) >= 200000 || 'Deal size must be at least $200,000',
      ]"
      validate-on-blur
    />

    <label>Loan Type</label>
    <v-select
      v-model="formData.loanType"
      solo chips deletable-chips dense
      :items="questions.loanTypes"
      item-value="name"
      item-text="title"
    />

    <label>Loan Term</label>
    <v-select
      v-model="formData.loanPaymentTerms"
      solo chips deletable-chips dense
      :items="questions.loanRepaymentTypes"
      item-value="name"
      item-text="title"
    />

    <label>Credit Score</label>
    <v-select
      v-model="formData.creditScore"
      solo chips deletable-chips dense
      :items="creditScoreOptions"
      item-value="name"
      item-text="title"
      :rules="required"
      validate-on-blur
    />

    <label>Lender Features</label>
    <v-select
      v-model="formData.lenderFeatures"
      solo chips deletable-chips multiple dense
      :items="lenderFeatureOptions"
      item-value="name"
      item-text="title"
    />

    <v-checkbox
      v-model="formData.hasRegulatoryIssues"
      class="mt-0 mb-2"
      label="Has Regulatory Issues (backruptcy)"
      hide-details
    />

    <v-checkbox
      v-model="formData.hasDisclosureEvents"
      class="mt-0 mb-5"
      label="Has Disclosure Events"
      hide-details
    />

    <label>Speed of Closing</label>
    <v-select
      v-model="formData.speedOfClosing"
      solo chips deletable-chips dense
      :items="deadlineSpeedsOptions"
      item-value="name"
      item-text="title"
      :rules="required"
    />

    <button
      class="default-btn blue"
      :disabled="loading">
      Submit
    </button>
  </v-form>
</template>

<script>
import { mapState } from 'vuex'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

import { formMixin } from '@/mixins'

const currencyMask = createNumberMask({
  prefix: '',
  includeThousandsSeparator: true,
  allowNegative: false,
  integerLimit: 18,
})

export default {
  name: 'QuestionsForm',
  mixins: [formMixin],
  props: {
    loading: Boolean,
  },

  data: () => ({
    formData: {
      dealType: [],
      practiceType: '',
      dealSize: '',
      loanType: '',
      loanPaymentTerms: '',
      creditScore: '',
      lenderFeatures: [],
      hasRegulatoryIssues: false,
      hasDisclosureEvents: false,
      speedOfClosing: '',
    },
    currencyMask,
  }),

  computed: {
    ...mapState('questionnaireStore', {
      questions: 'questions',
    }),

    dealTypeOptions () {
      const exclusiveTypes = [
        'External Acquisition (buy out)',
        'Internal Acquisition (buy in)',
        'Recruiting',
      ]

      const exclusive = exclusiveTypes.find(type => this.formData.dealType.includes(type))

      if (exclusive) {
        return this.questions.dealTypes.filter(({ name }) => {
          return !exclusiveTypes.includes(name) || name === exclusive
        })
      }

      return this.questions.dealTypes || []
    },

    creditScoreOptions () {
      return this.questions.creditScores.map(({ name, title, range }) => ({
        name,
        title: `${title} (${range})`,
      }))
    },

    lenderFeatureOptions () {
      if (this.formData.lenderFeatures.length >= 3) {
        return this.formData.lenderFeatures
      }

      const { lenderFeatures } = this.questions

      const ignoreIfSBA = [
        'Valuation optional',
        'Earn-outs allowed',
        'Seller is permitted to remain involved past 12 months',
        'No cash required to close',
      ]

      const ignoreIfConv = [
        'Finance transactional books of business',
      ]

      if (this.formData.loanType === 'SBA') {
        return lenderFeatures.filter(({ name }) => !ignoreIfSBA.includes(name))
      }

      if (this.formData.loanType === 'Conv') {
        return lenderFeatures.filter(({ name }) => !ignoreIfConv.includes(name))
      }

      return lenderFeatures
    },

    deadlineSpeedsOptions () {
      const ignoreIfSBA = [
        'Less than 30 days',
      ]

      if (this.formData.loanType === 'SBA') {
        return this.questions.deadlineSpeeds.filter(({ name }) => {
          return !ignoreIfSBA.includes(name)
        })
      }

      return this.questions.deadlineSpeeds
    },
  },

  methods: {
    submit () {
      if (!this.$refs.form.validate()) return

      this.$emit('submit', {
        ...this.formData,
        dealSize: parseInt(this.formData.dealSize.replace(/,/g, '')),
      })
    },
  },
}
</script>

<style lang="scss" scoped></style>
